import { useEffect, useState } from "react";
import { endpoints } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { X } from "@phosphor-icons/react";

const fmt = (n, d = 4) =>
  n === undefined || n === null
    ? "-"
    : Number(n).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });

const confColor = (c) =>
  c >= 70 ? "text-signal-buy" : c >= 50 ? "text-signal-warn" : "text-signal-sell";

const ConfCell = ({ pos }) =>
  (pos.ai_mode === "neurobro" || pos.ai_mode === "claude") && pos.score != null ? (
    <span className={confColor(Number(pos.score))}>{Math.round(pos.score)}%</span>
  ) : (
    <span className="text-muted-foreground">-</span>
  );

const MiniProgress = ({ p }) => {
  const { entry_price: entry, tp_price: tp, sl_price: sl, current_price: cur, side, symbol } = p;
  if (!entry || !tp || !sl || !cur) return null;
  // t: 0 = SL, 1 = TP (profit direction always to the right)
  const t = (price) =>
    side === "LONG" ? (price - sl) / (tp - sl) : (sl - price) / (sl - tp);
  const clamp = (v) => Math.max(0, Math.min(1, v));
  const tEntry = clamp(t(entry));
  const tCur = clamp(t(cur));
  const inProfit = tCur >= tEntry;
  const fillLeft = Math.min(tEntry, tCur) * 100;
  const fillWidth = Math.abs(tCur - tEntry) * 100;
  return (
    <div className="w-[110px] ml-auto" data-testid={`progress-${symbol}`}>
      <div className="relative h-1.5 rounded-full bg-[#1c1c1c] border border-border/60 overflow-visible">
        <div
          className={`absolute top-0 h-full ${inProfit ? "bg-signal-buy/70" : "bg-signal-sell/70"}`}
          style={{ left: `${fillLeft}%`, width: `${fillWidth}%` }}
        />
        <div
          className="absolute top-[-2px] w-[2px] h-[10px] bg-zinc-400"
          style={{ left: `calc(${tEntry * 100}% - 1px)` }}
          title={`Entry ${Number(entry).toFixed(4)}`}
        />
        <div
          className={`absolute top-[-2.5px] w-[7px] h-[7px] rounded-full border ${
            inProfit
              ? "bg-signal-buy border-signal-buy shadow-[0_0_5px_rgba(16,185,129,0.9)]"
              : "bg-signal-sell border-signal-sell shadow-[0_0_5px_rgba(239,68,68,0.9)]"
          }`}
          style={{ left: `calc(${tCur * 100}% - 3.5px)`, top: "-2.5px" }}
          title={`Current ${Number(cur).toFixed(4)}`}
        />
      </div>
      <div className="flex justify-between text-[8px] font-mono text-muted-foreground mt-1 leading-none">
        <span className="text-signal-sell/80">SL</span>
        <span data-testid={`progress-pct-${symbol}`}>{Math.round(tCur * 100)}%</span>
        <span className="text-signal-buy/80">TP</span>
      </div>
    </div>
  );
};

export const PositionsTable = ({ onChange }) => {
  const [positions, setPositions] = useState([]);
  const [closing, setClosing] = useState(null);

  const load = async () => {
    try {
      const { data } = await endpoints.positions();
      setPositions(data.positions || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line

  const closeOne = async (id) => {
    setClosing(id);
    try {
      await endpoints.closePosition(id);
      toast.success("Position closed");
      await load();
      onChange?.();
    } catch (e) {
      toast.error("Failed to close");
    } finally {
      setClosing(null);
    }
  };

  const closeAll = async () => {
    if (!window.confirm(`Close SEMUA ${positions.length} posisi yang sedang berjalan?`)) return;
    setClosing("ALL");
    try {
      const { data } = await endpoints.closeAllPositions();
      toast.success(`${data.closed} posisi ditutup`);
      await load();
      onChange?.();
    } catch (e) {
      toast.error("Gagal close all positions");
    } finally {
      setClosing(null);
    }
  };

  return (
    <div className="border border-border rounded-sm bg-[#0d0d0d]" data-testid="positions-table">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="font-heading font-bold text-sm">Open Positions</div>
        <div className="flex items-center gap-3">
          <div className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
            {positions.length} active
          </div>
          {positions.length > 0 && (
            <Button
              data-testid="close-all-positions-btn"
              onClick={closeAll}
              disabled={closing === "ALL"}
              variant="destructive"
              size="sm"
              className="rounded-sm h-7 text-[11px] font-mono uppercase tracking-wider"
            >
              <X size={12} className="mr-1" />
              {closing === "ALL" ? "Closing..." : "Close All Positions"}
            </Button>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="text-[10px] font-mono uppercase text-muted-foreground border-b border-border">
              <th className="text-left px-4 py-2">Symbol</th>
              <th className="text-left px-4 py-2">Side</th>
              <th className="text-right px-4 py-2">Conf</th>
              <th className="text-right px-4 py-2">Entry</th>
              <th className="text-right px-4 py-2">Current</th>
              <th className="text-right px-4 py-2">Qty</th>
              <th className="text-right px-4 py-2">Lev</th>
              <th className="text-right px-4 py-2">TP</th>
              <th className="text-right px-4 py-2">SL</th>
              <th className="text-right px-4 py-2">SL ↔ TP</th>
              <th className="text-right px-4 py-2">PnL</th>
              <th className="text-right px-4 py-2">Action</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {positions.length === 0 && (
              <tr>
                <td colSpan={11} className="text-center py-8 text-muted-foreground">
                  No open positions
                </td>
              </tr>
            )}
            {positions.map((p) => {
              const pnlPos = p.unrealized_pnl >= 0;
              return (
                <tr
                  key={p.id}
                  className="border-b border-border hover:bg-[#141414]"
                  data-testid={`position-${p.symbol}`}
                >
                  <td className="px-4 py-3 font-bold">
                    {p.symbol}
                    {p.partial_taken && (
                      <span
                        className="ml-2 text-[9px] px-1 py-0.5 rounded-sm border border-signal-buy/40 text-signal-buy bg-signal-buy/10 font-mono"
                        title={`Partial ${Math.round((p.partial_close_ratio || 0.5) * 100)}% closed at ${Number(p.partial_exit_price || 0).toFixed(4)}`}
                      >
                        PT
                      </span>
                    )}
                    {p.be_locked && !p.partial_taken && (
                      <span
                        className="ml-2 text-[9px] px-1 py-0.5 rounded-sm border border-signal-warn/40 text-signal-warn bg-signal-warn/10 font-mono"
                        title="Break-even locked"
                      >
                        BE
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded-sm ${
                        p.side === "LONG"
                          ? "bg-signal-buy/10 text-signal-buy"
                          : "bg-signal-sell/10 text-signal-sell"
                      }`}
                    >
                      {p.side}
                    </span>
                  </td>
                  <td className="text-right px-4 py-3" data-testid={`conf-${p.symbol}`}>
                    <ConfCell pos={p} />
                  </td>
                  <td className="text-right px-4 py-3">{fmt(p.entry_price)}</td>
                  <td className="text-right px-4 py-3">{fmt(p.current_price)}</td>
                  <td className="text-right px-4 py-3">{fmt(p.qty)}</td>
                  <td className="text-right px-4 py-3">{p.leverage}x</td>
                  <td className="text-right px-4 py-3 text-signal-buy/80">
                    <div>{fmt(p.tp_price)}</div>
                    <div className="text-[10px] opacity-70" data-testid={`tp-pct-${p.symbol}`}>
                      {p.entry_price ? `${(((p.tp_price - p.entry_price) / p.entry_price) * 100 * (p.side === "LONG" ? 1 : -1)).toFixed(2)}%` : ""}
                    </div>
                  </td>
                  <td className="text-right px-4 py-3 text-signal-sell/80">
                    <div>{fmt(p.sl_price)}</div>
                    <div className="text-[10px] opacity-70" data-testid={`sl-pct-${p.symbol}`}>
                      {p.entry_price ? `${(((p.sl_price - p.entry_price) / p.entry_price) * 100 * (p.side === "LONG" ? 1 : -1)).toFixed(2)}%` : ""}
                    </div>
                  </td>
                  <td className="text-right px-4 py-3 align-middle">
                    <MiniProgress p={p} />
                  </td>
                  <td className={`text-right px-4 py-3 ${pnlPos ? "text-signal-buy" : "text-signal-sell"}`}>
                    {pnlPos ? "+" : ""}${fmt(p.unrealized_pnl, 2)}
                    <div className="text-[10px] opacity-70">
                      {pnlPos ? "+" : ""}{fmt(p.unrealized_pnl_pct, 2)}%
                    </div>
                  </td>
                  <td className="text-right px-4 py-3">
                    <Button
                      data-testid={`close-${p.symbol}-btn`}
                      onClick={() => closeOne(p.id)}
                      disabled={closing === p.id}
                      size="sm"
                      variant="outline"
                      className="h-7 border-signal-sell/40 text-signal-sell hover:bg-signal-sell/10 hover:text-signal-sell rounded-sm"
                    >
                      <X size={12} />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PositionsTable;
